/**
 * IndexedDB storage layer for ArchiMate diagrams
 * Supports multiple diagrams with folder organization
 * Now workspace-scoped for multi-workspace support
 */

import type { ArchiMateDiagram } from '../types';
import type { ExtendedArchiMateDiagram } from '../extended-types';
import { ensureExtendedDiagram } from '../diagram-utils';
import { getCurrentWorkspaceId } from './workspace-db';

const DB_NAME = 'archimate-workspaces';
const DB_VERSION = 3;
const WORKSPACE_STORE = 'workspaces';
const DIAGRAM_STORE = 'diagrams';
const FOLDER_STORE = 'folders';

export interface Folder {
  id: string;
  workspaceId: string;
  name: string;
  parentId: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface DiagramMetadata {
  id: string;
  workspaceId: string;
  name: string;
  folderId: string | null;
  elementCount: number;
  relationshipCount: number;
  thumbnail?: string;
  createdAt: number;
  updatedAt: number;
}

// Initialize IndexedDB
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Workspaces store
      if (!db.objectStoreNames.contains(WORKSPACE_STORE)) {
        const workspaceStore = db.createObjectStore(WORKSPACE_STORE, { keyPath: 'id' });
        workspaceStore.createIndex('type', 'type', { unique: false });
        workspaceStore.createIndex('isDefault', 'isDefault', { unique: false });
      }

      // Diagrams store (workspace-scoped)
      if (!db.objectStoreNames.contains(DIAGRAM_STORE)) {
        const diagramStore = db.createObjectStore(DIAGRAM_STORE, { keyPath: 'id' });
        diagramStore.createIndex('workspaceId', 'workspaceId', { unique: false });
        diagramStore.createIndex('folderId', 'folderId', { unique: false });
        diagramStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }

      // Folders store (workspace-scoped)
      if (!db.objectStoreNames.contains(FOLDER_STORE)) {
        const folderStore = db.createObjectStore(FOLDER_STORE, { keyPath: 'id' });
        folderStore.createIndex('workspaceId', 'workspaceId', { unique: false });
        folderStore.createIndex('parentId', 'parentId', { unique: false });
      }
    };
  });
}

// Diagram operations
export async function saveDiagram(diagram: ArchiMateDiagram, folderId: string | null = null, workspaceId?: string): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(DIAGRAM_STORE, 'readwrite');
  const store = tx.objectStore(DIAGRAM_STORE);

  // Get current workspace if not provided
  const currentWorkspaceId = workspaceId || getCurrentWorkspaceId() || 'default';

  const metadata: DiagramMetadata = {
    id: diagram.id,
    workspaceId: currentWorkspaceId,
    name: diagram.name,
    folderId,
    elementCount: diagram.elements.length,
    relationshipCount: diagram.relationships.length,
    createdAt: diagram.createdAt,
    updatedAt: Date.now(),
  };

  await store.put({ ...diagram, ...metadata });
}

export async function loadDiagram(id: string): Promise<ExtendedArchiMateDiagram | null> {
  const db = await openDB();
  const tx = db.transaction(DIAGRAM_STORE, 'readonly');
  const store = tx.objectStore(DIAGRAM_STORE);

  return new Promise((resolve) => {
    const request = store.get(id);
    request.onsuccess = () => {
      const diagram = request.result;
      if (!diagram) {
        resolve(null);
      } else {
        // Ensure the loaded diagram has all extended properties
        resolve(ensureExtendedDiagram(diagram));
      }
    };
    request.onerror = () => resolve(null);
  });
}

export async function getAllDiagrams(workspaceId?: string): Promise<DiagramMetadata[]> {
  const db = await openDB();
  const tx = db.transaction(DIAGRAM_STORE, 'readonly');
  const store = tx.objectStore(DIAGRAM_STORE);

  // Get current workspace if not provided
  const currentWorkspaceId = workspaceId || getCurrentWorkspaceId() || 'default';

  return new Promise((resolve) => {
    const index = store.index('workspaceId');
    const request = index.getAll(currentWorkspaceId);
    request.onsuccess = () => {
      const diagrams = request.result.map((d: any) => ({
        id: d.id,
        workspaceId: d.workspaceId,
        name: d.name,
        folderId: d.folderId,
        elementCount: d.elementCount,
        relationshipCount: d.relationshipCount,
        thumbnail: d.thumbnail,
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
      }));
      resolve(diagrams);
    };
    request.onerror = () => resolve([]);
  });
}

export async function deleteDiagram(id: string, workspaceId?: string): Promise<void> {
  // Check if this is a remote workspace
  const currentWorkspaceId = workspaceId || getCurrentWorkspaceId() || 'default';
  const workspace = await getWorkspaceInfo(currentWorkspaceId);

  if (workspace?.type === 'remote' && workspace.d1DatabaseId) {
    // Delete via API for remote workspaces
    const response = await fetch(
      `/api/secure/archimate/workspaces/${currentWorkspaceId}/diagrams/${id}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      throw new Error('Failed to delete diagram from remote workspace');
    }
  }

  // Always delete from local IndexedDB cache
  const db = await openDB();
  const tx = db.transaction(DIAGRAM_STORE, 'readwrite');
  const store = tx.objectStore(DIAGRAM_STORE);
  await store.delete(id);
}

// Helper to get workspace info
async function getWorkspaceInfo(workspaceId: string): Promise<{ type: string; d1DatabaseId?: string } | null> {
  if (typeof window === 'undefined') return null;

  try {
    const response = await fetch(`/api/secure/archimate/workspaces`);
    if (!response.ok) return null;

    const workspaces = await response.json();
    return workspaces.find((w: any) => w.id === workspaceId) || null;
  } catch {
    return null;
  }
}

// Folder operations
export async function createFolder(name: string, parentId: string | null = null, workspaceId?: string): Promise<Folder> {
  const db = await openDB();
  const tx = db.transaction(FOLDER_STORE, 'readwrite');
  const store = tx.objectStore(FOLDER_STORE);

  // Get current workspace if not provided
  const currentWorkspaceId = workspaceId || getCurrentWorkspaceId() || 'default';

  const folder: Folder = {
    id: crypto.randomUUID(),
    workspaceId: currentWorkspaceId,
    name,
    parentId,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  await store.put(folder);
  return folder;
}

export async function getAllFolders(workspaceId?: string): Promise<Folder[]> {
  const db = await openDB();
  const tx = db.transaction(FOLDER_STORE, 'readonly');
  const store = tx.objectStore(FOLDER_STORE);

  // Get current workspace if not provided
  const currentWorkspaceId = workspaceId || getCurrentWorkspaceId() || 'default';

  return new Promise((resolve) => {
    const index = store.index('workspaceId');
    const request = index.getAll(currentWorkspaceId);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => resolve([]);
  });
}

export async function deleteFolder(id: string): Promise<void> {
  const db = await openDB();
  const tx = db.transaction([FOLDER_STORE, DIAGRAM_STORE], 'readwrite');

  // Delete folder
  const folderStore = tx.objectStore(FOLDER_STORE);
  await folderStore.delete(id);

  // Move diagrams in this folder to null (root)
  const diagramStore = tx.objectStore(DIAGRAM_STORE);
  const index = diagramStore.index('folderId');
  const request = index.getAll(id);

  request.onsuccess = async () => {
    const diagrams = request.result;
    for (const diagram of diagrams) {
      diagram.folderId = null;
      await diagramStore.put(diagram);
    }
  };
}
